import { useEffect } from "react";
import { isMobileApp } from "@sharpana/shared/utils/web_environment";
import {
  notifyRequestPayment,
  preloadPurchaseProducts,
} from "@sharpana/shared/utils/webViewBridge";
import { useOfferings } from "./useOfferings";
import type { ProductCatalog, RcProduct, Store } from "./types";

export interface UsePurchaseResult {
  /** True when running inside the native WebView wrapper. */
  isInMobileApp: boolean;
  /** Which store the wrapper reported, or undefined on web. */
  store: Store | undefined;
  /** Store metadata (localised price string, intro price, period) for a catalog key. */
  productFor: (key: string) => RcProduct | undefined;
  /**
   * Ask the native layer to purchase a catalog entry.
   *
   * Returns false when there is no native layer to ask — the caller then falls
   * back to its own web path (for boilit, submitting the Stripe form).  The
   * boolean is the whole point: it keeps the native/web fork in one place
   * instead of duplicating `isMobileApp()` checks per button.
   */
  requestNativePayment: (key: string) => boolean;
}

/**
 * The purchase mechanism shared by every product's paywall.
 *
 * Deliberately headless: paywall layout, copy and the product bullets differ
 * per product and stay in the app.  What must NOT differ is this — the
 * handshake with the native wrapper, which is wire contract.
 */
export function usePurchase(catalog: ProductCatalog): UsePurchaseResult {
  const offerings = useOfferings();
  const isInMobileApp = isMobileApp();

  // Warm the native product cache so the first tap doesn't wait on the store.
  useEffect(() => {
    if (!isInMobileApp) return;
    try {
      preloadPurchaseProducts(
        Object.values(catalog).map((p) => ({
          productIds: p.purchaseIds,
          productType: p.productType,
        }))
      );
    } catch {
      /* best-effort: a cold cache only costs latency on the first tap */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInMobileApp]);

  const store: Store | undefined = offerings?.store;

  const productFor = (key: string): RcProduct | undefined => {
    const entry = catalog[key];
    if (!entry || !store) return undefined;
    return offerings?.productsById?.[entry.pricingIds[store]];
  };

  const requestNativePayment = (key: string): boolean => {
    const entry = catalog[key];
    if (!entry || !isInMobileApp) return false;
    notifyRequestPayment(entry.purchaseIds, entry.productType);
    return true;
  };

  return { isInMobileApp, store, productFor, requestNativePayment };
}
